import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Tag, Percent, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ProductSalesTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/product-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId, updates) => {
    setSaving(prev => ({ ...prev, [productId]: true }));
    try {
      await axios.put(`${API_URL}/admin/product-sales/${productId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.product_id === productId ? { ...p, ...updates } : p
      ));
      
      toast.success('Product updated');
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setSaving(prev => ({ ...prev, [productId]: false }));
    }
  };

  const toggleSale = (product) => {
    updateProduct(product.product_id, {
      product_id: product.product_id,
      on_sale: !product.on_sale,
      sale_percentage: product.sale_percentage || 0,
      sale_label: product.sale_label || 'SALE'
    });
  };

  const updateSaleDetails = (product, field, value) => {
    const updates = {
      product_id: product.product_id,
      on_sale: product.on_sale,
      sale_percentage: product.sale_percentage || 0,
      sale_label: product.sale_label || 'SALE',
      [field]: value
    };
    updateProduct(product.product_id, updates);
  };

  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    const category = product.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  const categoryNames = {
    shades: 'Shades',
    drapery: 'Drapery',
    motorization: 'Motorization',
    other: 'Other'
  };

  const [activeCategory, setActiveCategory] = useState('all');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const activeCount = products.filter(p => p.on_sale).length;
  const categoryKeys = Object.keys(groupedProducts);

  const visibleProducts = activeCategory === 'all' ? products : (groupedProducts[activeCategory] || []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">Product Sales Badges</h3>
          <p className="text-sm text-neutral-500">
            {products.length} products · {activeCount} on sale
          </p>
        </div>
        <Button onClick={fetchProducts} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200 text-center">
          <div className="text-xl font-bold text-neutral-900">{products.length}</div>
          <div className="text-xs text-neutral-500">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
          <div className="text-xl font-bold text-green-600">{activeCount}</div>
          <div className="text-xs text-green-700">On Sale</div>
        </div>
        <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200 text-center">
          <div className="text-xl font-bold text-neutral-900">{products.length - activeCount}</div>
          <div className="text-xs text-neutral-500">Regular</div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 bg-neutral-100 rounded-lg p-1" data-testid="product-sales-tabs">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeCategory === 'all' ? 'bg-white shadow text-neutral-800' : 'text-neutral-500 hover:text-neutral-700'}`}
        >
          All ({products.length})
        </button>
        {categoryKeys.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeCategory === cat ? 'bg-white shadow text-neutral-800' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            {categoryNames[cat] || cat} ({groupedProducts[cat].length})
          </button>
        ))}
      </div>

      {/* Compact Product Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-neutral-600">Product</th>
              <th className="text-center px-3 py-2 font-medium text-neutral-600 w-20">Sale</th>
              <th className="text-center px-3 py-2 font-medium text-neutral-600 w-24">Label</th>
              <th className="text-center px-3 py-2 font-medium text-neutral-600 w-24">Discount</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visibleProducts.map(product => (
              <tr key={product.product_id} className={product.on_sale ? 'bg-green-50/50' : 'bg-white'}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{product.product_name}</span>
                    {product.on_sale && (
                      <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full font-bold">
                        {product.sale_label || 'SALE'}{product.sale_percentage > 0 ? ` ${product.sale_percentage}%` : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-neutral-400 capitalize">{product.category}</span>
                </td>
                <td className="text-center px-3 py-2.5">
                  <button
                    onClick={() => toggleSale(product)}
                    disabled={saving[product.product_id]}
                    className={`w-10 h-5 rounded-full transition-colors relative inline-block ${product.on_sale ? 'bg-green-500' : 'bg-neutral-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${product.on_sale ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </td>
                <td className="text-center px-3 py-2.5">
                  {product.on_sale && (
                    <select
                      value={product.sale_label || 'SALE'}
                      onChange={e => updateSaleDetails(product, 'sale_label', e.target.value)}
                      className="text-xs border rounded px-1.5 py-1 w-full"
                    >
                      <option value="SALE">SALE</option>
                      <option value="HOT">HOT</option>
                      <option value="NEW">NEW</option>
                      <option value="DEAL">DEAL</option>
                      <option value="LIMITED">LIMITED</option>
                      <option value="SPECIAL">SPECIAL</option>
                    </select>
                  )}
                </td>
                <td className="text-center px-3 py-2.5">
                  {product.on_sale && (
                    <div className="flex items-center justify-center gap-0.5">
                      <Input
                        type="number" min="0" max="90"
                        value={product.sale_percentage || ''}
                        onChange={e => updateSaleDetails(product, 'sale_percentage', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        className="w-14 text-xs h-7 text-center"
                      />
                      <span className="text-xs text-neutral-400">%</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductSalesTab;
